import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Switch,
    FormControlLabel,
    Checkbox,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Select,
    MenuItem,
    FormControl,
    IconButton,
    Divider,
    Stack,
    Paper,
    CircularProgress,
    Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TranslateIcon from '@mui/icons-material/Translate';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { CircleFlag } from 'react-circle-flags';

const PreTranslateModal = ({
    open,
    onClose,
    languages, // Array of { code, name, countryCode, projectId, translationService, tone, glossaryId }
    onLaunch,
    isLaunching,
    mtUsage = 0,
    mtQuota = 0,
    isOwner = false
}) => {
    const [useTM, setUseTM] = useState(true);
    const [useAI, setUseAI] = useState(true);
    const [selectedLangs, setSelectedLangs] = useState([]);
    const [langSettings, setLangSettings] = useState({}); // { projectId: { service: 'deepl' } }

    useEffect(() => {
        if (open && languages.length > 0) {
            setSelectedLangs(languages.map(l => l.projectId));
            const initialSettings = {};
            languages.forEach(l => {
                initialSettings[l.projectId] = {
                    service: l.translationService || 'deepl'
                };
            });
            setLangSettings(initialSettings);
        }
    }, [open, languages]);

    const handleToggleLang = (projectId) => {
        setSelectedLangs(prev =>
            prev.includes(projectId)
                ? prev.filter(id => id !== projectId)
                : [...prev, projectId]
        );
    };

    const handleServiceChange = (projectId, service) => {
        setLangSettings(prev => ({
            ...prev,
            [projectId]: { ...prev[projectId], service }
        }));
    };

    const isQuotaReached = !isOwner && mtUsage >= mtQuota;

    const handleLaunch = () => {
        if (useAI && isQuotaReached) return;
        onLaunch({
            useTM,
            tmConfirmation: 'confirmed', // Defaulting to confirmed for simplified toggle
            useAI,
            selectedProjects: selectedLangs.map(id => ({
                projectId: id,
                service: langSettings[id]?.service
            }))
        });
    };

    const selectedLanguageNames = languages
        .filter(l => selectedLangs.includes(l.projectId))
        .map(l => l.name)
        .join(', ');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, p: 1 }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Pre-Translate</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Translate your document automatically in seconds
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ mt: 2 }}>
                {/* 1. Pre-Approved Translations (TM) */}
                <Paper
                    variant="outlined"
                    sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'grey.50' }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 24 }}>1.</Typography>
                        <Box flex={1}>
                            <Typography variant="subtitle1" fontWeight={600}>Translation Memory</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Use existing translations if available
                            </Typography>
                        </Box>
                        <Switch
                            checked={useTM}
                            onChange={(e) => setUseTM(e.target.checked)}
                            color="primary"
                        />
                    </Stack>
                </Paper>

                {/* 2. AI Machine Translation */}
                <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2 }}
                >
                    <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 24 }}>2.</Typography>
                        <Box flex={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                AI Machine Translation
                            </Typography>
                            {!isOwner && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: isQuotaReached ? 'error.main' : 'text.secondary',
                                        fontWeight: 600
                                    }}
                                >
                                    {Math.max(0, mtQuota - mtUsage).toLocaleString()} characters remaining
                                </Typography>
                            )}
                        </Box>
                        <Switch
                            checked={useAI && !isQuotaReached}
                            onChange={(e) => setUseAI(e.target.checked)}
                            disabled={isQuotaReached}
                            color="primary"
                        />
                    </Stack>
                    {isQuotaReached && (
                        <Typography variant="caption" color="error" sx={{ ml: 5, display: 'block', mt: -1, mb: 1 }}>
                            Quota reached. Contact your team manager to increase your limit.
                        </Typography>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ ml: 5, mb: 2 }}>
                        Segments not found in the Translation Memory will be machine-translated.
                    </Typography>

                    <List sx={{ ml: 4, pt: 0 }}>
                        {languages.map((lang) => (
                            <ListItem
                                key={lang.projectId}
                                sx={{ py: 0.5, px: 1 }}
                            >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                    <Checkbox
                                        edge="start"
                                        checked={selectedLangs.includes(lang.projectId)}
                                        tabIndex={-1}
                                        disableRipple
                                        onChange={() => handleToggleLang(lang.projectId)}
                                        size="small"
                                    />
                                </ListItemIcon>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                    <CircleFlag countryCode={(lang.countryCode || 'un').toLowerCase()} height="20" />
                                    <ListItemText
                                        primary={lang.name}
                                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                    />
                                </Box>
                                <FormControl variant="standard" size="small" sx={{ minWidth: 80 }}>
                                    <Select
                                        value={langSettings[lang.projectId]?.service || 'deepl'}
                                        onChange={(e) => handleServiceChange(lang.projectId, e.target.value)}
                                        disableUnderline
                                        sx={{
                                            fontSize: '0.875rem',
                                            fontWeight: 600,
                                            color: 'primary.main',
                                            textAlign: 'right',
                                            '& .MuiSelect-select': { pr: 3 }
                                        }}
                                        IconComponent={KeyboardArrowDownIcon}
                                    >
                                        <MenuItem value="deepl">DeepL</MenuItem>
                                        <MenuItem value="google">Google</MenuItem>
                                        <MenuItem value="openai">OpenAI</MenuItem>
                                    </Select>
                                </FormControl>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    fullWidth
                    sx={{ borderRadius: 2, py: 1, textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleLaunch}
                    variant="contained"
                    fullWidth
                    color="primary"
                    disabled={isLaunching || (selectedLangs.length === 0) || (useAI && isQuotaReached)}
                    startIcon={isLaunching ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{ borderRadius: 2, py: 1, textTransform: 'none' }}
                >
                    {isLaunching ? 'Translating...' : (useAI && isQuotaReached) ? 'Quota Reached' : 'Launch pre-translation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PreTranslateModal;
